import { Routes } from '@angular/router';
import { LoginComponent } from './login/login.component';
import { AppLayoutComponent } from './layouts/app-layout/app-layout.component';
import { AuthGuard } from './auth/auth.guard';
import { ErrorComponent } from '@app/error/error.component';
import { VenuesIndexComponent } from './venues/index/venues-index.component';
import { VenueEditComponent } from './venues/edit/venue-edit.component';
import { VenueResolver } from './venues/shared/venue.resolver';
import { VenueViewComponent } from './venues/view/venue-view.component';
import { ProductsIndexComponent } from './products/index/products-index.component';
import { UsersIndexComponent } from './users/index/users-index.component';
import { SuppliersIndexComponent } from './suppliers/index/suppliers-index.component';
import { ProductEditComponent } from './products/edit/product-edit.component';
import { ProductViewComponent } from './products/view/product-view.component';
import { ProductResolver } from './products/shared/product-resolver';
import { UserEditComponent } from './users/edit/user-edit.component';
import { UserResolver } from './users/shared/user-resolver';
import { SupplierEditComponent } from './suppliers/edit/supplier-edit.component';
import { SupplierResolver } from './suppliers/shared/supplier-resolver';
import { EventsIndexComponent } from './events/index/events-index.component';
import { EventEditComponent } from './events/edit/event-edit.component';
import { EventOverviewResolver } from './events/shared/event-overview-resolver';
import { AuthLayoutComponent } from '@app/layouts/auth-layout/auth-layout.component';
import { unsavedChangesGuard } from '@app/shared/guards/unsaved-changes.guard';
import { EventViewComponent } from '@app/events/view/event-view.component';
import { EventTimelineItemResolver } from '@app/events/shared/event-timeline-item-resolver';
import { EventsCalendarComponent } from '@app/calendar/events-calendar.component';
import { BoardComponent } from '@app/board/view/board.component';
import { SalesDashboardComponent } from '@app/sales-dashboard/view/sales-dashboard.component';
import { AiSalesAssistantComponent } from '@app/ai-sales-assistant/ai-sales-assistant.component';
import { AiEventsAssistantComponent } from '@app/ai-events-assistant/ai-events-assistant.component';
import { FeatureToggleGuard } from '@app/feature-toggles/feature-toggle.guard';
import { PaymentRemediationComponent } from '@app/payment-remediation/index/payment-remediation.component';
import { EventHistoryLookupPageComponent } from '@app/event-history-lookup/event-history-lookup-page.component';
import { VenuePricesCalendarComponent } from '@app/venues/prices/venue-prices-calendar.component';
import { ProductPricesCalendarComponent } from '@app/products/prices/product-prices-calendar.component';
import { ProductVariantOptionPricesCalendarComponent } from '@app/products/prices/product-variant-option-prices-calendar.component';
import { ProductVariantOptionResolver } from '@app/products/shared/product-variant-option.resolver';
import { ContactsIndexComponent } from '@app/contacts/index/contacts-index.component';
import { ContactEditComponent } from '@app/contacts/edit/contact-edit.component';
import { ContactResolver } from '@app/contacts/shared/contact.resolver';
import { ShowRoundsPageComponent } from '@app/show-rounds/show-rounds-page.component';
import { CommunicationsPageComponent } from '@app/communications/communications-page.component';
import { EventTemplatesIndexComponent } from '@app/event-templates/index/event-templates-index.component';
import { EventTemplateEditComponent } from '@app/event-templates/edit/event-template-edit.component';
import { EventTemplateViewComponent } from '@app/event-templates/view/event-template-view.component';
import { EventTemplateResolver } from '@app/event-templates/shared/event-template.resolver';
import { FinancialBreakdownPageComponent } from '@app/events/financial-breakdown/financial-breakdown-page.component';
import { ForgotPasswordComponent } from '@app/auth/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from '@app/auth/reset-password/reset-password.component';
import { ApiCallLogsPageComponent } from '@app/api-logs/api-call-logs-page.component';
import { FeatureTogglesPageComponent } from '@app/feature-toggles/index/feature-toggles-page.component';
import { SettingsPageComponent } from '@app/settings/index/settings-page.component';
import { QuotesIndexComponent } from './quotes/index/quotes-index.component';
import { QuoteViewComponent } from './quotes/view/quote-view.component';
import { VenueQuoteResolver } from './quotes/shared/venue-quote.resolver';
import { ReportsDashboardComponent } from './reports/dashboard/reports-dashboard.component';
import { ReportCreateComponent } from './reports/create/report-create.component';
import { environment } from '../environments/environment';

export const routes: Routes = [
  // Routes with AuthLayout
  {
    path: '',
    component: AuthLayoutComponent, // Wrapping auth routes with AuthLayoutComponent
    children: [
      { path: 'login', component: LoginComponent },
      { path: 'forgot-password', component: ForgotPasswordComponent },
      { path: 'reset-password', component: ResetPasswordComponent },
      // TVG-43: Client Access invitation link target. The email links to
      // /set-password?invitation=<token>; the reset-password screen detects the
      // `invitation` query param and switches into invitation mode.
      { path: 'set-password', component: ResetPasswordComponent },
      // TVG-125: prospect lands here when they click the V1 quote-confirmation
      // email. The component redeems the `?token=` and routes onwards.
      {
        path: 'quote-login',
        loadComponent: () => import('./customer/quote-login/quote-login.component')
          .then(m => m.QuoteLoginComponent),
      },
      // TVG-339: the post-viewing rating page. The customer lands here from
      // the "Share your feedback" link in the review request three hours
      // after their viewing. No sign-in: the `?token=` is the authorisation,
      // the same arrangement /quote-login uses.
      {
        path: 'viewing-review',
        loadComponent: () => import('./customer/viewing-review/viewing-review.component')
          .then(m => m.ViewingReviewComponent),
      },
      { path: '', redirectTo: '/login', pathMatch: 'full' },
    ],
    data: { roles: ['*'] },
  },

  // Routes with AppLayout
  {
    path: '',
    component: AppLayoutComponent, // Wrapping app routes with AppLayoutComponent
    children: [
      // Dashboard
      {
        path: 'error',
        component: ErrorComponent,
        canActivate: [AuthGuard],
        data: { roles: ['*'] },
      },

      // Venues
      {
        path: 'venues',
        component: VenuesIndexComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
      },
      {
        path: 'venues/:id/edit',
        component: VenueEditComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
        resolve: { venue: VenueResolver },
      },
      {
        path: 'venues/:id/prices/calendar',
        component: VenuePricesCalendarComponent,
        resolve: {
          venue: VenueResolver,
        },
      },
      {
        path: 'venues/:id/view',
        component: VenueViewComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
        resolve: { venue: VenueResolver },
      },
      {
        path: 'venues/create',
        component: VenueEditComponent,
        data: { roles: ['admin'] },
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
      },

      // Products
      {
        path: 'products',
        component: ProductsIndexComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
      },
      {
        path: 'products/:id/edit',
        component: ProductEditComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        resolve: { product: ProductResolver },
        runGuardsAndResolvers: 'paramsChange',
        data: { roles: ['admin'] },
      },
      {
        path: 'products/:id/prices/calendar',
        component: ProductPricesCalendarComponent,
        canActivate: [AuthGuard],
        resolve: { product: ProductResolver },
        data: { roles: ['admin'] },
      },
      {
        path: 'products/:id/view',
        component: ProductViewComponent,
        canActivate: [AuthGuard],
        resolve: { product: ProductResolver },
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
      },
      {
        path: 'products/create',
        component: ProductEditComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin'] },
      },

      // Product Variant Options
      {
        path: 'product-variant-options/:id/prices/calendar',
        component: ProductVariantOptionPricesCalendarComponent,
        canActivate: [AuthGuard],
        resolve: { variantOption: ProductVariantOptionResolver },
        data: { roles: ['admin'] },
      },

      // Users
      {
        path: 'users',
        component: UsersIndexComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin'] },
      },

      {
        path: 'users/:id/edit',
        component: UserEditComponent,
        resolve: { user: UserResolver },
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin'] },
      },
      {
        path: 'users/create',
        component: UserEditComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin'] },
      },

      // TVG-337: Show rounds — every booking on its own line, the list form of
      // the show rounds TVG-324 shows on a contact record. Behind the same
      // `crm-sales-flow` toggle as the rest of the viewing stack.
      {
        path: 'show-rounds',
        component: ShowRoundsPageComponent,
        canActivate: [AuthGuard, FeatureToggleGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'], featureToggle: 'crm-sales-flow' },
      },

      // TVG-343: Communications — every message across every contact in one
      // list, plus writing and sending an email. Behind the `communications`
      // toggle, off by default on production, so with it off there is no page.
      {
        path: 'communications',
        component: CommunicationsPageComponent,
        canActivate: [AuthGuard, FeatureToggleGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'], featureToggle: 'communications' },
      },

      // Contacts
      {
        path: 'contacts',
        component: ContactsIndexComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
      },
      {
        path: 'contacts/:id/edit',
        component: ContactEditComponent,
        resolve: { user: ContactResolver },
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin', 'manager'] },
      },
      {
        path: 'contacts/create',
        component: ContactEditComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin', 'manager'] },
      },
      // TVG-136: read View of the contact page — same component/layout as the
      // edit route with `readOnly` turning editing off. Declared after
      // `contacts/create` so the static path is not captured as an :id, and
      // open to staff (read-only) as well as admin/manager.
      {
        path: 'contacts/:id',
        component: ContactEditComponent,
        resolve: { user: ContactResolver },
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'], readOnly: true },
      },

      {
        path: 'suppliers',
        component: SuppliersIndexComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin'] },
      },
      {
        path: 'suppliers/:id/edit',
        component: SupplierEditComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin'] },
        resolve: { supplier: SupplierResolver },
      },
      {
        path: 'suppliers/create',
        component: SupplierEditComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin'] },
      },

      // Event Templates
      {
        path: 'event-templates',
        component: EventTemplatesIndexComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager'] },
      },
      {
        path: 'event-templates/new',
        component: EventTemplateEditComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin'] },
        resolve: { template: EventTemplateResolver },
      },
      {
        path: 'event-templates/:id',
        component: EventTemplateViewComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
        resolve: { template: EventTemplateResolver },
      },
      {
        path: 'event-templates/:id/edit',
        component: EventTemplateEditComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin'] },
        resolve: { template: EventTemplateResolver },
      },

      // Events
      {
        path: 'events',
        component: EventsIndexComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
      },
      {
        path: 'events/create',
        component: EventEditComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        // TVG-352: raising an event is for admins and managers (mirrors
        // EventPolicy::create); standard users are excluded.
        data: { roles: ['admin', 'manager'] },
      },
      {
        path: 'events/:id',
        component: EventViewComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
        resolve: { event: EventOverviewResolver },
      },
      {
        path: 'events/:id/timeline/:timelineItemId',
        component: EventViewComponent,
        canActivate: [AuthGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
        resolve: { event: EventOverviewResolver, timelineItem: EventTimelineItemResolver },
      },
      {
        path: 'events/:id/financial-breakdown',
        component: FinancialBreakdownPageComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
      },

      // Calendar
      {
        path: 'events-calendar',
        component: EventsCalendarComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'] },
      },

      // Board
      {
        path: 'board',
        component: BoardComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager'] },
      },

      // TVG-236: Sales Dashboard V1. Contact-centric analytics + drill-down
      // worklist, gated behind the `sales-dashboard` feature toggle (404s when
      // off, so the page is invisible on prod until an admin enables it).
      {
        path: 'sales-dashboard',
        component: SalesDashboardComponent,
        canActivate: [AuthGuard, FeatureToggleGuard],
        data: { roles: ['admin', 'manager'], featureToggle: 'sales-dashboard' },
      },

      // TVG-147: Sales AI POC — AI Sales Assistant. Staff side only, gated behind
      // the `sales-ai` feature toggle (404s when off, so the page is invisible on
      // prod until an admin enables it).
      {
        path: 'ai-sales-assistant',
        component: AiSalesAssistantComponent,
        canActivate: [AuthGuard, FeatureToggleGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'], featureToggle: 'sales-ai' },
      },

      // TVG-150: AI Events Assistant — staff-side hub pointing the same engine at
      // live confirmed events (Payments and risk, Upsell). Gated behind the same
      // `sales-ai` feature toggle (404s when off, invisible on prod until enabled).
      {
        path: 'ai-events-assistant',
        component: AiEventsAssistantComponent,
        canActivate: [AuthGuard, FeatureToggleGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'], featureToggle: 'sales-ai' },
      },

      // TVG-176: Live payment remediation page. Staff/finance only, gated behind
      // the `payment-remediation` feature toggle (404s when off, so the page is
      // invisible on prod until an admin enables it).
      {
        path: 'payment-remediation',
        component: PaymentRemediationComponent,
        canActivate: [AuthGuard, FeatureToggleGuard],
        data: { roles: ['admin', 'manager', 'staff', 'salesperson'], featureToggle: 'payment-remediation' },
      },

      // TVG-220: Event History Lookup. Admin-only, deliberately absent from the
      // sidebar — reachable by direct URL only. Gated behind the
      // `event-history-lookup` feature toggle (404s when off, so the page is
      // invisible on prod until an admin enables it).
      {
        path: 'event-history-lookup',
        component: EventHistoryLookupPageComponent,
        canActivate: [AuthGuard, FeatureToggleGuard],
        data: { roles: ['admin'], featureToggle: 'event-history-lookup' },
      },

      // Quotes
      {
        path: 'quotes',
        component: QuotesIndexComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager'] },
      },
      {
        path: 'quotes/:id',
        component: QuoteViewComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'manager'] },
        resolve: { quote: VenueQuoteResolver },
      },
      {
        path: 'reports',
        component: ReportsDashboardComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin'] },
      },
      {
        path: 'reports/create/:type',
        component: ReportCreateComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin'] },
      },

      // Hidden maintenance route: API call logs
      {
        path: 'ops/api-call-logs',
        component: ApiCallLogsPageComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin'] },
      },

      // TVG-100: Feature toggles admin
      {
        path: 'admin/feature-toggles',
        component: FeatureTogglesPageComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin'] },
      },

      // TVG-315: editable settings admin (the text permission opt-in wording).
      {
        path: 'admin/settings',
        component: SettingsPageComponent,
        canActivate: [AuthGuard],
        data: { roles: ['admin'] },
      },
      // TVG-170: combined client home — the single place a client lands whether
      // they arrive as a prospect from a quote email or as an invited client
      // for a confirmed event. Lists every quote and event tied to their email.
      {
        path: 'my-home',
        canActivate: [AuthGuard],
        data: { roles: ['customer'], hideSidebar: true },
        loadComponent: () => import('./customer/home/customer-home.component')
          .then(m => m.CustomerHomeComponent),
      },
      {
        path: 'my-events',
        canActivate: [AuthGuard],
        data: { roles: ['customer'], hideSidebar: true },
        children: [
          // TVG-170: the standalone events list is replaced by the combined
          // home. The per-event detail page stays — the confirmed event on the
          // home links through to it.
          {
            path: '',
            pathMatch: 'full',
            redirectTo: '/my-home',
          },
          {
            path: ':id',
            canDeactivate: [unsavedChangesGuard],
            resolve: { event: EventOverviewResolver },
            loadComponent: () => import('./customer/view/customer-event-view.component')
              .then(m => m.CustomerEventViewComponent)
          },
        ]
      },
      // TVG-170: the standalone quotes list is replaced by the combined home.
      {
        path: 'my-quotes',
        pathMatch: 'full',
        redirectTo: '/my-home',
      },
    ],
  },

  // Wildcard route
  { path: '**', redirectTo: '/login' },
];
